import { Text } from "@imminently/react/ui/core/text";
import { grey } from "@imminently/react/ui/themes/imminently";
import { Badge, Box, IconButton, Stack, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import find from "lodash/find";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router";

import { FallRiskIcon } from "@/assets/icons";
import { useEncounterCommentModal } from "@/modals/encounterComment";
import { Icon } from "@/components/Icon";
import { ActionIcon, getStatusColor } from "@/components/Action";
import ATSScore from "@/components/ATSScore";
import PatientTaskButton, { TaskTooltip } from "@/components/PatientTaskButton";
import RiskScore from "@/components/RiskScore";
import TooltipText from "@/components/TooltipText";
import {
  calculateAgeMoment,
  formatDoB,
  formatDuration,
  getActiveTasks,
  getDOB,
  getTimeFromNow,
  getSex,
  getShortSex,
  useCurrentDateTime,
  getEncounterStatusForDisplaying,
} from "@/global";
import { SYMPTOM_LABELS } from "@/resources/constants";
import { getLengthOfStay, getTimeSinceTriage, getTimeToTriage } from "@/resources/encounters";
import { ClinicalRisk, Note, PatientEncounter, Task } from "@/resources/types";
import { css, yellow, green } from "@/theme";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { encounterService } from "@/services";
import { useComplaintCodes } from "@/components/ComplaintCode/data";
import { flattenComplaintCodeTree } from "@/components/ComplaintCode/util";

const styles = {
  status: css`
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;
    background-color: #c3c8de;
    text-transform: capitalize;
    &[data-distressed="true"] {
      color: white;
      background-color: #ff4b42;
    }
  `,
  alert: css`
    border-radius: 0.5rem;
    color: #6b201c;
    background-color: #ff8680;
    line-height: 1rem;
    min-height: 1rem;
    min-width: 1rem;
    align-items: center;
    justify-content: center;
  `,
  task: css`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.5rem;
    padding: 0.25rem;
    min-width: 2rem;
  `,
};

export const StatusCell = ({ row }: { row: PatientEncounter }) => {
  const { distressed } = row;
  const { statusList, tooltip, displayStatus } = getEncounterStatusForDisplaying(row);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Text
        css={styles.status}
        bold
        variant="caption"
        data-distressed={distressed}
      >
        {displayStatus}
      </Text>
      {statusList.length > 1 && (
        <Tooltip title={tooltip} placement="top" arrow>
          <Text css={styles.status} bold variant="caption">
            ...
          </Text>
        </Tooltip>
      )}
    </Stack>
  );
};

const AlertCellContent = ({ row }: { row: PatientEncounter }) => {
  const { patient, infectiousSymptoms, frailty = 0 } = row;
  const alerts = [];

  if (patient?.allergies && patient.allergies.length > 0) {
    const allAllergies = patient.allergies.map((a) => a.description).join(", ");
    alerts.push(`Allergies (${allAllergies})`);
  }

  if (infectiousSymptoms && infectiousSymptoms.length > 0) {
    const symptoms = infectiousSymptoms
      .map((symptom) => SYMPTOM_LABELS[symptom])
      .join(", ");

    alerts.push(symptoms ? `Infectious (${symptoms})` : "Infectious");
  }

  // TODO frailty score for alert is TBD
  if (frailty > 7) alerts.push("Frail");

  if (alerts.length === 0) return null;

  const tooltip = (
    <Stack alignItems="center">
      {alerts.map((item, index) => (
        <div key={index}>{item}</div>
      ))}
    </Stack>
  );

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Stack css={styles.alert} m={1}>
        <Text variant="caption">{alerts.length}</Text>
      </Stack>
    </Tooltip>
  );
};

export const AlertCell = ({ row }: { row: PatientEncounter }) => {
  return (
    <AlertCellContent row={row} />
  );
};

export const AtsCell = ({ value }: { value: number | undefined }) =>
  value ? <ATSScore score={value} /> : null;

export const TasksPendingCell = ({ value }: { value: string }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    {value}
  </Stack>
);

export const getSexAgeValue = (_: any, row: any) => {
  const patient = row as PatientEncounter;
  const dobInfo = getDOB(patient);
  const dob = dobInfo.fromPatient || dobInfo.fromDelegate;
  const age = dob ? calculateAgeMoment(dob) : "-";

  return `${getShortSex(patient)} (${age})`;
};

export const SexAgeCell = (params: GridRenderCellParams) => {
  const patient = params.row as PatientEncounter;
  const dobInfo = getDOB(patient);
  const dob = dobInfo.fromPatient || dobInfo.fromDelegate;
  const sexInfo = getSex(patient);
  const delegateInfo = [];
  let warning = false;

  if (dobInfo.fromDelegate) {
    if (dobInfo.fromPatient) {
      delegateInfo.push(`Date of Birth: ${formatDoB(dobInfo.fromDelegate)}`);
    } else {
      delegateInfo.push("Date of Birth");
      warning = true;
    }
  }

  if (sexInfo.fromDelegate) {
    if (sexInfo.fromPatient) {
      delegateInfo.push(`Sex: ${sexInfo.fromDelegate}`);
    } else {
      delegateInfo.push("Sex");
      warning = true;
    }
  }

  // Hide underline if delegate provided no info
  const tooltipSx =
    delegateInfo.length === 0 ? { borderBottom: "none" } : undefined;

  return (
    <TooltipText
      text={params.value}
      sx={tooltipSx}
      warning={warning}
    >
      <Stack alignItems="center" spacing={1.5}>
        <Stack alignItems="center">
          <Text bold variant="subtitle1">
            Date Of Birth
          </Text>
          <Text>{dob ? formatDoB(dob) : "Unknown"}</Text>
        </Stack>

        {delegateInfo.length > 0 && (
          <Stack alignItems="center">
            <Text bold variant="subtitle1">
              Delegate provided
            </Text>
            {delegateInfo.map((text, index) => (
              <Text key={index}>{text}</Text>
            ))}
          </Stack>
        )}
      </Stack>
    </TooltipText>
  );
};

export const LocationCell = (_: any, row: any) => {
  const patient = row as PatientEncounter;
  // Locations sometime don't have a shortName, or they come as '' (which reverts to true when using an if)
  if (patient.location?.shortName && patient.location?.shortName !== "") return patient.location.shortName;
  else if (patient.location?.name) return patient.location?.name;
  else return "";
};

export const LengthOfStayCell = ({ row }: { row: PatientEncounter }) => {
  const losValue = getLengthOfStay(row);
  const timeToTriage = getTimeToTriage(row);
  const timeSinceTriage = getTimeSinceTriage(row);

  if (!losValue) return null;

  const duration = moment.duration(losValue);
  const los = formatDuration(duration);
  const tooltip = timeToTriage || timeSinceTriage ? (
    <Stack>
      {timeToTriage ? <Text>Time To Triage: {formatDuration(moment.duration(timeToTriage))}</Text> : null}
      {timeSinceTriage ? <Text>Time Since Triage: {formatDuration(moment.duration(timeSinceTriage))}</Text> : null}
    </Stack>
  ) : null;

  const value = (
    <Tooltip arrow placement="top" title={tooltip}>
      <Text>{los}</Text>
    </Tooltip>
  );

  if (duration.asHours() > 2) {
    const color = duration.asHours() > 4 ? "error" : "warning";

    return (
      <Badge variant="dot" color={color}>
        {value}
      </Badge>
    );
  }

  return value;
};

export const ObservationDueCell = ({ row }: { row: PatientEncounter }) => {
  const { nextObsDue } = row;
  const now = useCurrentDateTime();
  const navigate = useNavigate();

  if (row.status === "expected" || !nextObsDue) {
    return null;
  }

  const obsDue = moment(nextObsDue);
  const startTime = moment(now);
  const due = moment.duration(obsDue.diff(startTime));

  const handleClick = (event: any) => {
    event.stopPropagation();
    navigate({
      pathname: `/patients/${String(row.id)}`,
      search: new URLSearchParams({ tab: "observations-chart" }).toString(),
    });
  };

  let content;

  // changed requirement, fine to show a negative time, just show it in red/error
  if (due.asMinutes() <= 0) {
    content = <Text color="error.main">{formatDuration(due)}</Text>;
  } else {
    content = <Text>{formatDuration(due)}</Text>;
  }

  return (
    <Box onClick={handleClick} sx={{ cursor: "pointer" }}>
      {content}
    </Box>
  );
};

const ProblemText = ({ title, text }: { title: string; text?: string }) => {
  if (!text) return null;

  return (
    <Stack spacing={0.5}>
      <Text bold variant="subtitle1" sx={{ textTransform: "uppercase" }}>
        {title}
      </Text>
      <Text>{text ?? "N/A"}</Text>
    </Stack>
  );
};

export const PresentingProblemCell = ({ row }: { row: PatientEncounter }) => {
  const { problem } = row;
  const complaintCodes = useComplaintCodes();
  const complaintCode = (row as any).complaintCode;
  const complaintNode = useMemo(() => flattenComplaintCodeTree(complaintCodes).find(node => node.id === complaintCode), [complaintCodes, complaintCode]);
  const complaintCodeText = complaintNode?.path.join(" - ") || complaintCode;

  const fullTooltip = (
    <Stack spacing={2}>
      <ProblemText title="Complaint code" text={complaintCodeText} />
      <ProblemText title="Triage" text={problem?.triage} />
      <ProblemText title="Patient" text={problem?.patient} />
      <ProblemText title="Ambulance" text={problem?.ambulance} />
      <ProblemText title="Expects" text={problem?.expects} />
    </Stack>
  );

  const problemText =
    problem?.triage ||
    problem?.patient ||
    problem?.ambulance ||
    problem?.expects ||
    null;
  const mainText = [complaintCodeText, problemText].filter(Boolean).join(" ; ") || null;

  return (
    <Tooltip title={fullTooltip} placement="top" arrow>
      <Text noWrap>{mainText}</Text>
    </Tooltip>
  );
};

const NoteText = ({ note }: { note: Note }) => (
  <Stack spacing={0.5}>
    <Stack>
      <Text bold variant="subtitle1">
        {note.createdByName}
      </Text>
      <Text
        variant="caption"
        sx={{ fontSize: "0.5rem", textTransform: "uppercase" }}
      >
        {new Date(note.created).toDateString()}
      </Text>
    </Stack>
    <Text>{note.text}</Text>
  </Stack>
);

export const NotesCell = ({ row }: { row: PatientEncounter }) => {
  const { notes } = row;

  if (!notes || notes.length === 0) {
    return null;
  }

  // Use the latest note. Notes are already sorted from latest to oldest by
  // the backend.
  const note = notes[0];

  const fullTooltip = (
    <Stack spacing={2}>
      <NoteText key={note.createdBy} note={note} />
    </Stack>
  );

  return (
    <Tooltip title={fullTooltip} placement="top" arrow>
      <Text noWrap>{note.text}</Text>
    </Tooltip>
  );
};

const COMMENT_TRUNCATE_LEN = 40;

const getCommentText = (comment: PatientEncounter["comment"]): string =>
  comment?.text ?? "";

const getCommentSeverity = (comment: PatientEncounter["comment"]): "low" | "medium" | "high" =>
  comment?.severity === "high" || comment?.severity === "medium" ? comment.severity : "low";

const getCommentModifiedBy = (comment: PatientEncounter["comment"]): string | undefined =>
  comment?.modifiedBy?.name;

export const CommentCell = ({ row }: { row: PatientEncounter }) => {
  const openCommentModal = useEncounterCommentModal();
  const commentText = getCommentText(row.comment);
  const severity = getCommentSeverity(row.comment);
  const modifiedBy = getCommentModifiedBy(row.comment);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openCommentModal({ encounter: row });
  };

  if (commentText) {
    const display = commentText.length > COMMENT_TRUNCATE_LEN
      ? `${commentText.slice(0, COMMENT_TRUNCATE_LEN)}...`
      : commentText;
    const tooltipTitle = modifiedBy ? (
      <Stack spacing={0.5}>
        <span>{commentText}</span>
        <Text variant="caption" color="text.secondary">— {modifiedBy}</Text>
      </Stack>
    ) : commentText;
    const bgColor = severity === "high" ? "error.100" : severity === "medium" ? "warning.100" : undefined;
    return (
      <Tooltip title={tooltipTitle} placement="top" arrow>
        <Box
          onClick={handleClick}
          sx={{
            cursor: "pointer",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            bgcolor: bgColor,
          }}
        >
          <Text noWrap>{display}</Text>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Add comment" placement="top" arrow>
      <IconButton size="small" onClick={handleClick} sx={{ p: 0.25, "& svg": { fontSize: 14 } }}>
        <Icon icon="add" />
      </IconButton>
    </Tooltip>
  );
};

const ActionTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "primary.25",
      color: "primary.900",
      border: "1px solid",
      borderColor: "primary.100",
    },
  },
  arrow: {
    sx: {
      color: "primary.25",
      border: "1px solid",
      borderColor: "primary.100",
    },
  },
};

export const ActionsCell = ({ row, colDef }: { row: PatientEncounter; colDef: GridColDef }) => {
  const { tasks } = row;
  const { settings } = useSettingsContext();
  const taskDefinitions: any[] = (settings as any)?.taskDefinitions || [];
  const { mutateAsync: updateEncounter } = encounterService.useUpdate();

  const pendingTasksRef = useRef<Task[]>([]);

  const flushTaskUpdates = useMemo(() => debounce(() => {
    const tasksToSend = [...pendingTasksRef.current];
    pendingTasksRef.current = [];
    updateEncounter({ id: row.id, payload: { tasks: tasksToSend } });
  }, 1000), [row.id, updateEncounter]);

  const handleTaskChange = useCallback((task: Task) => {
    const idx = pendingTasksRef.current.findIndex(t => t.type === task.type);
    if (idx >= 0) {
      pendingTasksRef.current[idx] = task;
    } else {
      pendingTasksRef.current.push(task);
    }
    flushTaskUpdates();
  }, [flushTaskUpdates]);

  const renderTask = useCallback((task: Task) => {
    const def = taskDefinitions.find((td: any) => td.name === task.type);
    if (!def) return null;

    return (
      <PatientTaskButton
        key={task.type}
        value={task}
        onClick={handleTaskChange}
        small={true}
        taskDefinition={def}
      />
    );
  }, [taskDefinitions, handleTaskChange]);

  if (!tasks || tasks.length === 0) return null;

  const activeTasks = getActiveTasks(tasks);

  // 32 for each button + 8 for each gap
  const requiredWidth = activeTasks.length * 32 + (activeTasks.length - 1) * 8;
  const colWidth = colDef.width ?? 1;

  if (requiredWidth > colWidth) {
    const buttonWidthWithGap = 56;
    let numVisible = 0;

    const estimatePlusButtonWidth = (hiddenCount: number) => {
      const textWidth = (String(hiddenCount).length + 1) * 10;
      return 48 + textWidth + 8;
    };

    const safetyBuffer = 4;
    const availableWidth = colWidth - safetyBuffer;

    for (let i = 0; i < activeTasks.length; i++) {
      const spaceUsed = (i + 1) * buttonWidthWithGap;
      const hiddenCount = activeTasks.length - (i + 1);
      const plusButtonWidth = estimatePlusButtonWidth(hiddenCount);

      if (spaceUsed + plusButtonWidth <= availableWidth) {
        numVisible = i + 1;
      } else {
        break;
      }
    }

    numVisible = Math.max(0, numVisible);

    const tooltip = (
      <Stack direction="row" gap={1}>
        {activeTasks.slice(numVisible).map(renderTask)}
      </Stack>
    );

    return (
      <Stack direction="row" gap={1}>
        {activeTasks.slice(0, numVisible).map(renderTask)}
        <Tooltip
          key="more"
          title={tooltip}
          placement="top"
          slotProps={ActionTooltipSlotProps}
        >
          <Box css={styles.task} color="white" bgcolor="primary.700">
            <Text variant="subtitle1" sx={{ lineHeight: 1 }}>
              +{activeTasks.length - numVisible}
            </Text>
          </Box>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={1}>
      {activeTasks.map(renderTask)}
    </Stack>
  );
};

const getRiskColor = (risk: number) => {
  switch (risk) {
    case -1:
      return grey[500];
    case 0:
      return green[500];
    default:
      return yellow[500];
  }
};

/* eslint-disable-next-line complexity */
export const FallsCell = ({ row }: { row: PatientEncounter }) => {
  const risks = [];

  if (row.frailty && row.frailty >= 5) {
    risks.push("Self reported frailty");
  }

  if (row.fallRisk) {
    if (row.fallRisk.fallenInLastYear) risks.push("Fallen in last year");
    if (row.fallRisk.feelUnsteady) risks.push("Feels unsteady");
    if (row.fallRisk.worriedAboutFalling) risks.push("Worried about falling");
  }

  let riskCount;
  // IMPORTANT - if there are no risks and fall risk questions have not been answered
  // we want to display a special case of unknown
  if (risks.length === 0 && !row.fallRisk) riskCount = -1;
  else riskCount = risks.length;
  const color = getRiskColor(riskCount);

  // tooltip should read:
  // unknown if -1
  // no risk if 0
  // name of the risk(s) if > 0
  const tooltip = riskCount === -1
    ? "Unknown"
    : riskCount === 0
      ? "No risk"
      : risks.join(", ");

  return (
    <Stack direction="row" gap={1}>
      <Tooltip key="falls" title={tooltip} placement="bottom" arrow>
        <Box style={{ color }}>
          <FallRiskIcon width={24} height={24} />
        </Box>
      </Tooltip>
    </Stack>
  );
};

export const PractitionersCell = (type: string) => {
  const PractionerCell = ({ row }: { row: PatientEncounter }) => {
    const { practitioners } = row;
    if (!practitioners) return null;

    const prac = find(practitioners, { type: type });
    if (!prac) return null;

    return (
      <Tooltip title={prac.fullName || ""} placement="top" arrow>
        <Text>{prac.shortName}</Text>
      </Tooltip>
    );
  };

  return PractionerCell;
};

export const RiskScoreCell = ({ row }: { row: PatientEncounter }) => {
  const { riskScore, observations } = row;
  const navigate = useNavigate();
  // @ts-ignore
  const alt = (observations && observations.length > 0) ? <RiskScore riskScore={{ score: "-", risk: ClinicalRisk.LOW }} /> : null;

  if (!riskScore && !alt) {
    return null;
  }

  const handleClick = (event: any) => {
    event.stopPropagation();
    navigate({
      pathname: `/patients/${String(row.id)}`,
      search: new URLSearchParams({ tab: "observations-chart" }).toString(),
    });
  };

  return (
    <Box onClick={handleClick} sx={{ cursor: "pointer" }}>
      {riskScore ? <RiskScore riskScore={riskScore} /> : alt}
    </Box>
  );
};

export const ArrivalTimeCell = ({ row }: { row: PatientEncounter }) => {
  if (!row.arrived || row.status !== "expected") {
    return null;
  }

  const duration = getTimeFromNow(row.arrived);
  console.log('duration', duration);
  const value = duration.asSeconds() > 0 ? formatDuration(duration) : "0.00";
  const tooltip = moment(row.arrived).format("DD/MM/YYYY [at] h:mm a");

  return (
    <Tooltip arrow placement="top" title={tooltip}>
      <Text>{value}</Text>
    </Tooltip>
  );
};
